// @flow
import React, { Component } from 'react';
import StopEventPropagation from 'app/packages/stop-event-propagation/StopEventPropagation';
import { HEADER_DEFAULT_HEIGHT } from 'app/common/react/components/Header/HeaderConstants';
import createAnimationManager, {
  type AnimationManagerType,
} from 'app/common/lib/react/animationUtils';
import { Box, Layer } from 'gestalt';

const MAX_ZINDEX = 16777271; // https://stackoverflow.com/a/25461690/4056191
const SCROLL_ZONE_HEIGHT = 120;
const SLOW_SCROLL_SPEED = 0.8; // in pixels per millisecond
const FAST_SCROLL_SPEED = 1.5 * SLOW_SCROLL_SPEED;
const SLOW_SCROLL_DURATION = 500;
const VISIBLE_DELAY = 500; // delay is necessary to allow dragging from near the top/bottom

type DragScrollZoneProps = {
  height: number,
  placement: 'top' | 'bottom',
};

type DragScrollZoneState = {
  animation: ?AnimationManagerType,
  visible: boolean,
};

// Inspired by https://github.com/martindrapeau/jQueryDndPageScroll
class DragScrollZone extends Component<DragScrollZoneProps, DragScrollZoneState> {
  state: DragScrollZoneState = {
    animation: null,
    visible: false,
  };

  componentDidMount() {
    this.visibleTimeout = window.setTimeout(() => this.setState({ visible: true }), VISIBLE_DELAY);
  }

  componentWillUnmount() {
    const target = this.element;
    const { animation } = this.state;
    if (target) {
      target.removeEventListener('dragover', this.handleDragOver);
      target.removeEventListener('dragleave', this.handleDragLeave);
    }
    if (animation) {
      animation.cancel();
    }
    if (this.visibleTimeout) {
      window.clearTimeout(this.visibleTimeout);
    }
  }

  setRef = (ref: HTMLElement) => {
    if (!this.element) {
      this.element = ref;
      ref.addEventListener('dragover', this.handleDragOver);
      ref.addEventListener('dragleave', this.handleDragLeave);
    }
  };

  handleDragOver = () => {
    const { placement } = this.props;
    if (!this.state.animation) {
      const unitVector = placement === 'top' ? -1 : 1;
      const animation = createAnimationManager({
        window,
        duration: Infinity, // animation is explicitly canceled
        run: (progress, elapsedTime, elapsedTimeSinceLastRun) => {
          const speed = elapsedTime < SLOW_SCROLL_DURATION ? SLOW_SCROLL_SPEED : FAST_SCROLL_SPEED;
          window.scrollTo(0, window.scrollY + speed * unitVector * elapsedTimeSinceLastRun);
        },
      });
      animation.start();
      this.setState({ animation });
    }
  };

  handleDragLeave = () => {
    const { animation } = this.state;
    if (animation) {
      animation.cancel();
      this.setState({ animation: null });
    }
  };

  element: ?HTMLElement = null;
  visibleTimeout: ?TimeoutID = null;

  render() {
    const { height, placement } = this.props;
    const { visible } = this.state;
    return (
      <Box
        bottom={placement === 'bottom'}
        dangerouslySetInlineStyle={{
          __style: { zIndex: MAX_ZINDEX, visibility: visible ? 'visible' : 'hidden' },
        }}
        height={height}
        left
        position="fixed"
        right
        top={placement === 'top'}
      >
        {
          // $FlowFixMe: New error after Flow Upgrade http://pinch.pinadmin.com/flow53upgdoc
          <div ref={this.setRef} style={{ width: '100%', height: '100%' }} />
        }
      </Box>
    );
  }
}

type DragScrollManagerProps = {
  noStickyHeader?: boolean,
};

export default function DragScrollManager({ noStickyHeader }: DragScrollManagerProps) {
  const headerOffset = noStickyHeader ? 0 : HEADER_DEFAULT_HEIGHT;
  return (
    <StopEventPropagation>
      <Layer>
        <Box>
          <DragScrollZone placement="top" height={SCROLL_ZONE_HEIGHT + headerOffset} />
          <DragScrollZone placement="bottom" height={SCROLL_ZONE_HEIGHT} />
        </Box>
      </Layer>
    </StopEventPropagation>
  );
}
